// src/components/common/Sidebar.jsx
import React, { useContext, useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { ThemeContext } from '../../context/ThemeContext';
import { UserContext } from '../../context/UserContext';
import userService from '../../services/userService';

const Sidebar = () => {
  const { theme } = useContext(ThemeContext);
  const { userType, upgradeToPro, user } = useContext(UserContext);
  const navigate = useNavigate();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [stats, setStats] = useState({ notes: 0, flashcards: 0, aiUsed: 0, aiLimit: 0 });

  useEffect(() => {
    let mounted = true;
    const fetch = async () => {
      try {
        const res = await userService.getDashboard();
        const payload = res?.data || res;
        if (!mounted) return;
        const s = payload?.stats || payload || {};
        setStats({
          notes: s.notesCount ?? 0,
          flashcards: s.flashcardsCount ?? 0,
          aiUsed: s.aiSearchesUsed ?? 0,
          aiLimit: s.aiSearchLimit ?? 0,
        });
      } catch (err) {
        // keep defaults
      }
    };

    fetch();
    return () => { mounted = false; };
  }, []);

  const navItems = [
    { path: '/dashboard', icon: '📊', label: 'Dashboard' },
    { path: '/notes', icon: '📝', label: 'Notes' },
    { path: '/flashcards', icon: '🎴', label: 'Flashcards' },
    { path: '/search', icon: '🔍', label: 'Search Notes' },
    { path: '/ai-search', icon: '🤖', label: 'AI Search' },
    { path: '/profile', icon: '👤', label: 'Profile' },
    { path: '/settings', icon: '⚙️', label: 'Settings' },
  ];

  return (
    <aside className={`sidebar ${isCollapsed ? 'collapsed' : ''} ${theme === 'dark' ? 'dark' : ''} hidden md:block`}>
      <div className={`h-full border-r border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 transition-all duration-300 ${
        isCollapsed ? 'w-16 md:w-20' : 'w-64'
      }`}>
        <div className="p-4">
          {/* Collapse Toggle */}
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="mb-6 p-2 rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700"
          >
            {isCollapsed ? '→' : '←'}
          </button>

          {/* Navigation */}
          <nav className="space-y-2">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  `flex items-center p-3 rounded-lg transition ${
                    isActive
                      ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`
                }
              >
                <span className="text-xl mr-3">{item.icon}</span>
                {!isCollapsed && <span>{item.label}</span>}
              </NavLink>
            ))}
          </nav>

          {/* Pro Upgrade Banner (for free users) */}
          {userType === 'free' && !isCollapsed && (
            <div className="mt-8 p-4 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg text-white">
              <h3 className="font-bold mb-2">Upgrade to Pro</h3>
              <p className="text-sm mb-4">Unlock unlimited AI searches and premium features</p>
              <button
                onClick={upgradeToPro}
                className="w-full py-2 bg-white text-purple-600 font-bold rounded-lg hover:bg-gray-100 transition"
              >
                Upgrade Now
              </button>
            </div>
          )}

          {/* Quick Stats (when expanded) */}
          {!isCollapsed && (
            <div className="mt-8 pt-8 border-t border-gray-200 dark:border-gray-800">
              <h4 className="font-bold text-gray-700 dark:text-gray-300 mb-3">Quick Stats</h4>
              <div className="space-y-3">
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600 dark:text-gray-400">Notes</span>
                    <span className="font-bold">{stats.notes}{userType === 'free' ? '/10' : ''}</span>
                  </div>
                  {userType === 'free' && (
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                      <div 
                        className="bg-blue-600 h-1.5 rounded-full" 
                        style={{ width: `${(stats.notes / 10) * 100}%` }}
                      ></div>
                    </div>
                  )}
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600 dark:text-gray-400">Flashcards</span>
                    <span className="font-bold">{stats.flashcards}{userType === 'free' ? '/20' : ''}</span>
                  </div>
                  {userType === 'free' && (
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                      <div 
                        className="bg-green-600 h-1.5 rounded-full" 
                        style={{ width: `${(stats.flashcards / 20) * 100}%` }}
                      ></div>
                    </div>
                  )}
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600 dark:text-gray-400">AI Searches</span>
                    <span className="font-bold">{stats.aiUsed}/{stats.aiLimit || (user?.aiSearchLimit ?? 5)}</span>
                  </div>
                  {userType === 'free' && (
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                      <div 
                        className="bg-purple-600 h-1.5 rounded-full" 
                        style={{ width: `${(stats.aiUsed / (stats.aiLimit || 5)) * 100}%` }}
                      ></div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;